import { useCallback, useMemo } from 'react';

import { type GasPayload, type GasScreenEventPayload } from '@atlaskit/analytics-gas-types';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import FeatureGates from '@atlaskit/feature-gate-js-client';

import { ALL_PRODUCTS_STRING, DEFAULT_GAS_CHANNEL } from '../../../common/constants/analytics';
import { sha256Hash } from '../../../common/utils/events/utils';
import {
	useAnalyticsAttributes,
	useApplicationMode,
	useExperimentConfig,
	useFilterAttributesForTracking,
	useProductEdition,
	useQueryUpdatedTime,
	useQueryVersion,
	useSearchSessionId,
	useSelectedProduct,
} from '../../store';
import {
	useFetchEntitiesRequestId,
	useFetchSpacesRequestId,
	useQuickFindQuery,
} from '../../store/quick-find';

export const useQuickFindAnalytics = () => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const fireAnalyticsEvent = useCallback(
		(payload: GasPayload) => {
			createAnalyticsEvent({
				...payload,
				source: 'searchDialog',
			}).fire(DEFAULT_GAS_CHANNEL);
		},
		[createAnalyticsEvent],
	);

	const fireAnalyticsScreenEvent = useCallback(
		(payload: GasScreenEventPayload) => {
			createAnalyticsEvent({
				...payload,
				eventType: 'screen',
				source: 'searchDialog',
			}).fire(DEFAULT_GAS_CHANNEL);
		},
		[createAnalyticsEvent],
	);

	return { fireAnalyticsEvent, fireAnalyticsScreenEvent };
};

export const useQuickFindAttributes = () => {
	const [searchSessionId] = useSearchSessionId();
	const fetchEntitiesRequestId = useFetchEntitiesRequestId();
	const fetchSpacesRequestId = useFetchSpacesRequestId();
	// eslint-disable-next-line @atlassian/search-common/no-use-rss-hooks
	const [queryUpdatedTime] = useQueryUpdatedTime();
	const quickFindQuery = useQuickFindQuery();
	// eslint-disable-next-line @atlassian/search-common/no-use-rss-hooks
	const [queryVersion] = useQueryVersion();
	const [experimentConfig] = useExperimentConfig();
	const applicationMode = useApplicationMode();
	const [analyticsAttributes] = useAnalyticsAttributes();
	const [productSelected] = useSelectedProduct();
	const filters = useFilterAttributesForTracking();
	const [productEdition] = useProductEdition();

	return useMemo(() => {
		const { frontendExperiments = {}, combinedExperiment = {} } = experimentConfig;
		const { filterSelections, filters: filterCategories } = filters;

		const quickFind3pExperiment = FeatureGates.getExperiment(
			'quick_find_3p_search_results_experiment',
			{
				fireExperimentExposure: false,
			},
		);

		const commonAttributes = {
			productEdition,
			frontendExperiments: {
				activeExperiments: Object.keys(frontendExperiments).filter(
					(experiment) => frontendExperiments[experiment]?.serveExperiment,
				),
				...frontendExperiments,
				quick_find_3p_search_results_experiment: {
					abTestId: 'quick_find_3p_search_results_experiment',
					...quickFind3pExperiment.value,
				},
			},
			combinedExperiment,
			...getCommonQueryAttributes(quickFindQuery),
			queryUpdatedAgo: queryUpdatedTime ? Date.now() - queryUpdatedTime : undefined,
			queryVersion,
			searchSessionId,
			fetchEntitiesTraceId: fetchEntitiesRequestId,
			fetchSpacesTraceId: fetchSpacesRequestId,
			isQuickFind: true,
			mode: applicationMode,
			productSelected: productSelected || ALL_PRODUCTS_STRING,
			filters: filterCategories,
			filterSelections,
			...analyticsAttributes,
		};

		const nonPrivacySafeAttributes = {
			query: quickFindQuery,
		};

		return { commonAttributes, nonPrivacySafeAttributes };
	}, [
		analyticsAttributes,
		applicationMode,
		experimentConfig,
		fetchEntitiesRequestId,
		fetchSpacesRequestId,
		filters,
		productEdition,
		productSelected,
		queryUpdatedTime,
		queryVersion,
		quickFindQuery,
		searchSessionId,
	]);
};

export const getCommonQueryAttributes = (query: string) => {
	return {
		numberOfTerms: (query.match(/\w+/g) || []).length,
		queryHash: sha256Hash(query),
		queryHashSHA256: sha256Hash(query),
		queryLength: query.length,
		wordCount: (query.match(/\w+/g) || []).length,
	};
};
